import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { mergeRegister } from "@lexical/utils";
import {
  $getSelection,
  $isNodeSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  SELECTION_CHANGE_COMMAND,
} from "lexical";
import { createContext, useContext, useEffect, useState } from "react";
import { $isDynamicContentNode } from "./nodes/dynamic-content/dynamic-content-node";

import type { FC, ReactNode } from "react";

interface LexicalParentNodeContextType {
  dynamicContent: boolean;
}

const LexicalParentNodeContext = createContext<LexicalParentNodeContextType | null>(null);

export const LexicalParentNodeContextProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [editor] = useLexicalComposerContext();
  const [dynamicContent, setDynamicContent] = useState(false);

  useEffect(
    () =>
      mergeRegister(
        editor.registerCommand(
          SELECTION_CHANGE_COMMAND,
          () => {
            const selection = $getSelection();
            let insideDynamic = false;
            if ($isRangeSelection(selection)) {
              const node = selection.anchor.getNode();
              insideDynamic = node.getParents().some((parent) => $isDynamicContentNode(parent));
            } else if ($isNodeSelection(selection)) {
              const nodes = selection.getNodes();
              insideDynamic = nodes.some((node) => $isDynamicContentNode(node));
            }
            setDynamicContent(insideDynamic);
            return false; // Returning false to indicate that the command was not handled
          },
          COMMAND_PRIORITY_HIGH,
        ),
      ),
    [editor],
  );

  return (
    <LexicalParentNodeContext.Provider value={{ dynamicContent }}>
      {children}
    </LexicalParentNodeContext.Provider>
  );
};

export const useLexicalParentNodeContext = (): LexicalParentNodeContextType => {
  const context = useContext(LexicalParentNodeContext);
  if (!context) {
    throw new Error("useLexicalParentNodeContext must be used within a LexicalContextProvider");
  }
  return context;
};
